import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  SignPatientMultimediaRequestDTO,
} from 'dtos';
import ButtonGroup from '@mui/material/ButtonGroup';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import clsx from 'clsx';
import SignatureCanvas from 'react-signature-canvas';
import Controller from 'core/components/FormController';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  useSignPatientMultimedia,
} from 'views/EMR/hooks/media';
import Input from 'pages/Dashboard/components/Input';
import {
  useNotify,
} from 'core/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  Button,
} from '@populateui/ui';

type Props = {
  mediaId?: number;
  onClose: () => void;
  onComplete: () => void;
};

type Tab = 'draw' | 'type';
type FormData = { signatureValue: string };

export default function MultimediaSignatureDialog({
  mediaId,
  onClose,
  onComplete,
}: Props) {
  const { user } = useUserInfo() ?? {};
  const [tab, setTab] = useState<Tab>('type');
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const [isDrawingCompleted, setIsDrawingCompleted] = useState(false);

  const formProps = useForm<FormData>({
    defaultValues: {
      signatureValue: user?.formattedNames?.fullNameWithCredentials ?? '',
    },
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, isSubmitting },
  } = formProps;

  const { mutateAsync: signPatientMultimedia } = useSignPatientMultimedia();
  const notify = useNotify();

  const isDrawingMode = tab === 'draw';
  const isTypingMode = tab === 'type';

  const typedSignature = watch('signatureValue');
  const canSubmit = useMemo(
    () => (isTypingMode
      ? !isEmptyString(typedSignature)
      : isDrawingCompleted && !(signatureRef.current?.isEmpty?.() ?? true)),
    [isDrawingCompleted, tab, typedSignature],
  );

  const resetSignature = () => {
    if (isDrawingMode) {
      signatureRef.current?.clear();
      setIsDrawingCompleted(false);
    } else {
      setValue('signatureValue', '');
    }
  };

  const onSubmit = async (data: FormData) => {
    const payload: SignPatientMultimediaRequestDTO = {
      patientMultimediaId: mediaId,
      isImage: isDrawingMode,
      signatureValue: isDrawingMode
        ? (signatureRef.current?.toDataURL('image/png') ?? null)
        : data.signatureValue,
    };

    await signPatientMultimedia(payload);
    notify({
      variant: 'success',
      message: 'Multimedia is signed successfully',
    });
    onComplete();
    onClose();
  };

  const disabled = !canSubmit || isSubmitting || !isValid;

  return (
    <Dialog
      title="Sign Document"
      onClose={onClose}
      maxWidth="lg"
      className="populate-form !w-[27rem]"
      disableEscapeKeyDown
      disableBackdropClick
      open
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-form-row !mt-0 w-full">
          <ButtonGroup
            variant="outlined"
            color="inherit"
            aria-label="Signature mode"
          >
            <Button
              className={clsx(
                'flex gap-2 !h-8 !min-w-0 !px-3',
                { '!bg-blue-50 !text-blue-600': isTypingMode },
                { '!text-gray-600': !isTypingMode },
              )}
              type="button"
              variant="outlined"
              onClick={() => { setTab('type'); }}
            >
              Type
            </Button>
            <Button
              className={clsx(
                'flex gap-2 !h-8 !min-w-0 !px-3',
                { '!bg-blue-50 !text-blue-600': isDrawingMode },
                { '!text-gray-600': !isDrawingMode },
              )}
              type="button"
              variant="outlined"
              onClick={() => { setTab('draw'); }}
            >
              Draw
            </Button>
          </ButtonGroup>
          <div className="border border-gray-200 rounded-md p-4">
            <div className="relative h-32">
              {isDrawingMode ? (
                <div className="flex flex-col gap-form-row h-full">
                  <div
                    ref={canvasContainerRef}
                    className="h-36 rounded-md bg-white overflow-hidden"
                  >
                    <SignatureCanvas
                      ref={(ref) => { signatureRef.current = ref; }}
                      penColor="#345D7E"
                      onEnd={() => { setIsDrawingCompleted(true); }}
                      canvasProps={{
                        className: 'w-full h-full signature-canvas-pen-cursor',
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-form-row">
                  <Controller
                    control={control}
                    name="signatureValue"
                    render={({ field, fieldState: { invalid } }) => (
                      <Input
                        {...field}
                        id="signatureValue"
                        placeholder="Type your signature"
                        className="text-gray-900 text-xs"
                        error={invalid}
                        showErrorText={false}
                      />
                    )}
                  />
                  <div className="bg-gray-50 rounded-md px-4 flex items-center h-20">
                    <div
                      className="text-[#345D7E] w-full overflow-hidden text-ellipsis whitespace-nowrap text-center dancing-script-regular text-4xl"
                      title={typedSignature?.trim()}
                    >
                      {!isEmptyString(typedSignature) ? typedSignature : ''}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500 pt-3 mt-3 border-t border-gray-200">
              <div className="text-sm text-gray-500 truncate">
                Sign above
              </div>
              <button
                type="button"
                onClick={resetSignature}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="flex flex-row-reverse items-center justify-between gap-3 mt-2">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                className={clsx(
                  '!pointer-events-auto',
                  { '!cursor-not-allowed': disabled },
                )}
                color="primary"
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={disabled}
              >
                {isSubmitting ? 'Signing...' : 'Sign'}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
